import { useQuery } from "@tanstack/react-query";
import { api } from "../../services/api";
import { useEffect, useState } from "react";
import { ToggleSwitch } from "../../components/Switch";
import { ReactComponent as Bell } from "../../assets/icons/bell.svg";
import { LottieLoading } from "../../components/Loading/LottieLoading";



function NotificationToggle({
  name,
  checked,
  onChange,
  label,
}: {
  name: string;
  checked: boolean;
  onChange: (name: string, value: boolean) => void;
  label?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <label className="text-text3 text-xs">{label}</label>
      <ToggleSwitch name={name} checked={checked} onChange={onChange} />
    </div>
  )
}


function HeaderToggle({
  name,
  checked,
  onChange,
  children,
  label,
  icon,
  iconClassName,
}: {
  name: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  children: React.ReactNode;
  label?: string;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  iconClassName?: string;
}) {

  const Icon = icon || Bell;
  return (
    <>
      <div className="flex items-start gap-[18px]">

        <div className="relative bg-background4 w-[37px] h-[37px] rounded-full flex items-center justify-center">
          <Icon className={`w-[18px] h-[18px] icon ${iconClassName}`} />
        </div>
        <div className="ml-3 flex-1">
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-1">
              {label && <span className="text-black text-[15px] font-medium">{label}</span>}
              <label className="text-text3 font-medium text-[15px]">{name}</label>
            </div>
            <ToggleSwitch name={name} checked={checked} onChange={(_, value) => onChange(value)} />
          </div>
          {children}
        </div>
      </div>
    </>
  )
}

export function NotificationSettings() {
  const [notificationPreferences, setNotificationPreferences] = useState<any>(null);
  const [isPreferencesSaving, setIsPreferencesSaving] = useState(false);
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["user-preferences"],
    queryFn: () => api.getNotificationPreferences(),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const updateNotificationPreferences = (newPreferences: any) => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setIsPreferencesSaving(true);
    api.updateNotificationPreferences({ ...newPreferences, timezone: timeZone }).then((res) => {
      refetch();
      setIsPreferencesSaving(false);
    }).catch((err) => {
      setIsPreferencesSaving(false);
    });
  }



  const isSaving = isPreferencesSaving || isFetching;

  const handleChange = (name: string, value: boolean) => {
    if (isSaving) {
      return;
    }
    let newPreferences = { ...notificationPreferences };
    newPreferences[name] = value;
    setNotificationPreferences(newPreferences);
    updateNotificationPreferences(newPreferences);
  }

  const handleReminderNotificationChange = (value: string) => {
    if (isSaving) {
      return;
    }
    const newPreferences = { ...notificationPreferences };
    if (newPreferences.practiceReminder) {
      const hasValue = newPreferences.practiceReminder?.find((item: any) => item === value);
      if (hasValue) {
        newPreferences.practiceReminder = newPreferences.practiceReminder.filter((item: any) => item !== value);
      } else {
        newPreferences.practiceReminder.push(value);
      }
      setNotificationPreferences(newPreferences);
      updateNotificationPreferences(newPreferences);
    }
  }

  const handleAllReminderNotificationChange = () => {
    if (isSaving) {
      return;
    }
    const newPreferences = { ...notificationPreferences };
    if (newPreferences.practiceReminder?.length > 0) {
      newPreferences.practiceReminder = [];
    } else {
      newPreferences.practiceReminder = ["morning", "midday", "evening"];
    }
    setNotificationPreferences(newPreferences);
    updateNotificationPreferences(newPreferences);
  }

  useEffect(() => {
    setNotificationPreferences(data?.data);
  }, [data]);

  if (isLoading) {
    return <div className="flex justify-center items-center h-full">
      <LottieLoading />
    </div>
  }

  return (
    <div className="mt-4">
      <div className="flex flex-col gap-8">
        <HeaderToggle icon={Bell} iconClassName="[&_path]:fill-primary [&_path]:stroke-transparent" label="Reminder Notifications" name="Daily Practice Reminder" checked={notificationPreferences?.practiceReminder?.length > 0} onChange={handleAllReminderNotificationChange}>
          <div className="flex flex-col gap-2 mt-4">
            <NotificationToggle name="morning" label="Morning (8:00 AM)" checked={notificationPreferences?.practiceReminder?.includes("morning")} onChange={() => handleReminderNotificationChange("morning")} />
            <NotificationToggle name="midday" label="Midday (12:00 PM)" checked={notificationPreferences?.practiceReminder?.includes("midday")} onChange={() => handleReminderNotificationChange("midday")} />
            <NotificationToggle name="evening" label="Evening (6:00 PM)" checked={notificationPreferences?.practiceReminder?.includes("evening")} onChange={() => handleReminderNotificationChange("evening")} />
          </div>
        </HeaderToggle>
      </div>
    </div>
  )
}